import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, Paper, Stack, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Button, Avatar, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Divider, useTheme, alpha,
  Backdrop, CircularProgress, Snackbar,
  InputAdornment, Pagination, Badge,
  Card, CardContent, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AttachMoney as MoneyIcon,
  DateRange as DateRangeIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  FilterList as FilterIcon,
  BarChart as ChartIcon,
  TableChart as TableIcon,
  CurrencyExchange as NairaIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types
interface SaleRecord {
  id: number;
  item_id: string;
  item_name: string;
  category_id: string;
  category_name: string;
  qty_sold: number | string;
  cost: number | string;
  reported_by: string;
  recorded_by: string;
  reported_on: string;
  created_on: string;
  branch_id: string;
  business_id: string;
  reportedByName?: string;
  recordedByName?: string;
}

interface SalesSummary {
  category_id?: string;
  category_name?: string;
  item_id?: string;
  item_name?: string;
  total_qty: number | string;
  total_cost: number | string;
}

interface SalesItem {
  item_id: string;
  item_name: string;
  category_id: string;
  category_name: string;
}

interface ApiResponse {
  status: boolean;
  message: string;
  data: {
    status: boolean;
    data: any[];
  };
}

interface FilterOptions {
  startDate: Date | null;
  endDate: Date | null;
  categoryId: string;
  itemId: string;
  search: string;
}

interface Category {
  category_id: string;
  category_name: string;
}

interface InventoryItem {
  item_id: string;
  item_name: string;
  category_id?: string;
  category_name?: string;
}

type ViewMode = 'summary' | 'details';
type GroupBy = 'category' | 'item';

const SalesDashboardBFSupervisor: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  
  // View states
  const [viewMode, setViewMode] = useState<ViewMode>('summary');
  const [groupBy, setGroupBy] = useState<GroupBy>('category');
  
  // Data states
  const [salesRecords, setSalesRecords] = useState<SaleRecord[]>([]);
  const [salesSummary, setSalesSummary] = useState<SalesSummary[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [inventoryItems, setInventoryItems] = useState<SalesItem[]>([]);
  
  // Filter states
  const [filters, setFilters] = useState<FilterOptions>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)), // Last 30 days
    endDate: new Date(),
    categoryId: 'all',
    itemId: 'all',
    search: ''
  });
  
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Helper function to safely parse dates
  const safeParseDate = (dateString: string | undefined): Date => {
    if (!dateString) return new Date();
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', dateString);
      return new Date();
    }
    return date;
  };

  // Format date to YYYY-MM-DD
  const formatDate = (date: Date): string => {
    return format(date, 'yyyy-MM-dd');
  };

  // Parse number from string
  const parseNumber = (value: string | number | undefined): number => {
    if (value === undefined || value === null) return 0;
    if (typeof value === 'number') return value;
    return parseFloat(value) || 0;
  };

  // Calculate total revenue for a record (qty_sold * cost)
  const calculateTotalRevenue = (record: SaleRecord): number => {
    return parseNumber(record.qty_sold) * parseNumber(record.cost);
  };

  // Format currency with Naira symbol
  const formatNaira = (amount: number): string => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
      currencyDisplay: 'symbol'
    }).format(amount).replace('NGN', '₦');
  };

  // Fetch categories and items for filters
  const fetchFilterData = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post<ApiResponse>(
        `${baseUrl()}BeverageSupervisors/getSalesItems`, 
        formData, 
        config
      );
      
      console.log("Sales items response:", response.data);

      if (response.data.status) {
        // Access the nested data structure
        const items = response.data.data?.data || [];
        setInventoryItems(items);
        
        // Extract unique categories
        const uniqueCategories = new Map();
        items.forEach((item: SalesItem) => {
          if (item.category_id && !uniqueCategories.has(item.category_id)) {
            uniqueCategories.set(item.category_id, {
              category_id: item.category_id,
              category_name: item.category_name || 'Uncategorized'
            });
          }
        });
        setCategories(Array.from(uniqueCategories.values()));
      }
    } catch (error) {
      console.error("Fetch filter data error:", error);
    }
  };

  // Fetch sales summary
  const fetchSalesSummary = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      if (filters.startDate) {
        formData.append("startDate", formatDate(filters.startDate));
      }
      if (filters.endDate) {
        formData.append("endDate", formatDate(filters.endDate));
      }
      formData.append("groupBy", groupBy);
      
      if (filters.categoryId !== 'all') {
        formData.append("category_id", filters.categoryId);
      }
      if (filters.itemId !== 'all') {
        formData.append("item_id", filters.itemId);
      }

      const response = await axios.post<ApiResponse>(
        `${baseUrl()}BeverageSupervisors/getSalesSummary`,
        formData,
        config
      );
      
      console.log("Sales summary response:", response.data);

      if (response.data.status) {
        // Access the nested data structure
        const summaryData = response.data.data?.data || [];
        setSalesSummary(summaryData);
      }
    } catch (error) {
      console.error("Fetch sales summary error:", error);
      showNotification('Failed to fetch sales summary', 'error');
    }
  };

  // Fetch detailed sales records
  const fetchSalesDetails = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      if (filters.startDate) {
        formData.append("startDate", formatDate(filters.startDate));
      }
      if (filters.endDate) {
        formData.append("endDate", formatDate(filters.endDate));
      }
      
      if (filters.categoryId !== 'all') {
        formData.append("category_id", filters.categoryId);
      }
      if (filters.itemId !== 'all') {
        formData.append("item_id", filters.itemId);
      }

      const response = await axios.post<ApiResponse>(
        `${baseUrl()}BeverageSupervisors/getSales`,
        formData,
        config
      );
      
      console.log("Sales details response:", response.data);

      if (response.data.status) {
        // Access the nested data structure
        const recordsData = response.data.data?.data || [];
        setSalesRecords(recordsData);
      }
    } catch (error) {
      console.error("Fetch sales details error:", error);
      showNotification('Failed to fetch sales details', 'error');
    }
  };

  // Fetch all data
  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSalesSummary(),
        fetchSalesDetails()
      ]);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (field: keyof FilterOptions, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
      endDate: new Date(),
      categoryId: 'all',
      itemId: 'all',
      search: ''
    });
  };

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true);
    fetchAllData().finally(() => setRefreshing(false));
  };

  // Handle view mode change
  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: ViewMode | null
  ) => {
    if (newMode) {
      setViewMode(newMode);
      setPage(1);
    }
  };

  // Handle group by change
  const handleGroupByChange = (
    event: React.MouseEvent<HTMLElement>,
    newGroupBy: GroupBy | null
  ) => {
    if (newGroupBy) {
      setGroupBy(newGroupBy);
    }
  };

  // Apply search filter locally for details view
  const getFilteredRecords = (): SaleRecord[] => {
    // Ensure salesRecords is an array
    const records = Array.isArray(salesRecords) ? salesRecords : [];
    
    if (!filters.search) return records;
    
    const searchLower = filters.search.toLowerCase();
    return records.filter(record => 
      record.item_name?.toLowerCase().includes(searchLower) ||
      record.category_name?.toLowerCase().includes(searchLower) ||
      record.reportedByName?.toLowerCase().includes(searchLower)
    );
  };

  // Calculate totals
  const calculateTotals = () => {
    let totalQty = 0;
    let totalRevenue = 0;

    if (viewMode === 'summary') {
      const summary = Array.isArray(salesSummary) ? salesSummary : [];
      totalQty = summary.reduce((sum, item) => sum + parseNumber(item.total_qty), 0);
      totalRevenue = summary.reduce((sum, item) => sum + parseNumber(item.total_cost), 0);
    } else {
      const filtered = getFilteredRecords();
      totalQty = filtered.reduce((sum, record) => sum + parseNumber(record.qty_sold), 0);
      totalRevenue = filtered.reduce((sum, record) => sum + parseNumber(record.cost), 0);
    }

    return { totalQty, totalRevenue };
  };

  const { totalQty, totalRevenue } = calculateTotals();
  const filteredRecords = getFilteredRecords();
  const pageCount = Math.ceil(filteredRecords.length / rowsPerPage);
  const paginatedRecords = filteredRecords.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  useEffect(() => {
    fetchFilterData();
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [filters.startDate, filters.endDate, filters.categoryId, filters.itemId, viewMode, groupBy]);

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all' && v !== '').length;

  const exportToCSV = () => {
    try {
      let headers: string[] = [];
      let csvData: any[][] = [];

      if (viewMode === 'summary') {
        const summary = Array.isArray(salesSummary) ? salesSummary : [];
        
        if (groupBy === 'category') {
          headers = ['Category', 'Total Quantity', 'Total Revenue (₦)'];
          csvData = summary.map(item => [
            item.category_name || 'Uncategorized',
            parseNumber(item.total_qty),
            formatNaira(parseNumber(item.total_cost))
          ]);
        } else {
          headers = ['Item', 'Category', 'Total Quantity', 'Total Revenue (₦)'];
          csvData = summary.map(item => [
            item.item_name || 'Unknown',
            item.category_name || 'Uncategorized',
            parseNumber(item.total_qty),
            formatNaira(parseNumber(item.total_cost))
          ]);
        }
      } else {
        const records = getFilteredRecords();
        headers = ['Date', 'Item', 'Category', 'Quantity', 'Unit Price (₦)', 'Total Revenue (₦)', 'Reported By'];
        csvData = records.map(record => [
          format(safeParseDate(record.reported_on), 'yyyy-MM-dd'),
          record.item_name,
          record.category_name,
          parseNumber(record.qty_sold),
          formatNaira(parseNumber(record.cost)),
          formatNaira(calculateTotalRevenue(record)),
          record.reportedByName || 'Unknown'
        ]);
      }

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const viewName = viewMode === 'summary' ? 'summary' : 'details';
      link.setAttribute('href', url);
      link.setAttribute('download', `sales_${viewName}_${formatDate(new Date())}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showNotification('Report exported successfully', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showNotification('Failed to export report', 'error');
    }
  };

  const getDateRangeLabel = () => {
    if (filters.startDate && filters.endDate) {
      return `${formatDate(filters.startDate)} to ${formatDate(filters.endDate)}`;
    }
    return 'Select date range';
  };

  return (
    <IonPage>
      <IonContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
            
            {/* Header */}
            <Paper 
              elevation={0}
              sx={{ 
                borderBottom: `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'background.paper'
              }}
            >
              <Container maxWidth="xl">
                <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                  <IconButton onClick={() => history.goBack()}>
                    <BackIcon />
                  </IconButton>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main
                    }}
                  >
                    <DashboardIcon />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h5" fontWeight={600}>
                      Sales Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      View and analyze sales data
                    </Typography>
                  </Box>
                  <Chip 
                    label={getDateRangeLabel()}
                    icon={<DateRangeIcon />}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Tooltip title="Export to CSV">
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportToCSV}
                        disabled={(viewMode === 'summary' && (!salesSummary || salesSummary.length === 0)) || 
                                 (viewMode === 'details' && filteredRecords.length === 0)}
                        size="small"
                      >
                        Export
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Refresh">
                    <Button
                      variant="contained"
                      startIcon={<RefreshIcon />}
                      onClick={handleRefresh}
                      disabled={refreshing}
                      size="small"
                    >
                      Refresh
                    </Button>
                  </Tooltip>
                </Stack>

                {/* View Mode Toggle */}
                <Stack direction="row" spacing={2} sx={{ pb: 2 }}>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                  >
                    <ToggleButton value="summary">
                      <ChartIcon sx={{ mr: 1 }} />
                      Summary View
                    </ToggleButton>
                    <ToggleButton value="details">
                      <TableIcon sx={{ mr: 1 }} />
                      Details View
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {viewMode === 'summary' && (
                    <ToggleButtonGroup
                      value={groupBy}
                      exclusive
                      onChange={handleGroupByChange}
                      size="small"
                    >
                      <ToggleButton value="category">
                        <CategoryIcon sx={{ mr: 1 }} />
                        Group by Category
                      </ToggleButton>
                      <ToggleButton value="item">
                        <InventoryIcon sx={{ mr: 1 }} />
                        Group by Item
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                </Stack>
              </Container>
            </Paper>

            <Container maxWidth="xl" sx={{ py: 3 }}>
              
              {/* Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography color="text.secondary" variant="body2">
                            Total Quantity Sold
                          </Typography>
                          <Typography variant="h4">{totalQty}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                          <InventoryIcon />
                        </Avatar>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography color="text.secondary" variant="body2">
                            Total Revenue
                          </Typography>
                          <Typography variant="h4" color="success.main">
                            {formatNaira(totalRevenue)}
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                          <MoneyIcon />
                        </Avatar>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography color="text.secondary" variant="body2">
                            Average Transaction
                          </Typography>
                          <Typography variant="h4" color="primary.main">
                            {formatNaira(totalQty > 0 ? totalRevenue / totalQty : 0)}
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                          <TrendingUpIcon />
                        </Avatar>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography color="text.secondary" variant="body2">
                            {viewMode === 'summary' ? 'Categories/Items' : 'Transactions'}
                          </Typography>
                          <Typography variant="h4">
                            {viewMode === 'summary' 
                              ? (Array.isArray(salesSummary) ? salesSummary.length : 0)
                              : filteredRecords.length}
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                          {viewMode === 'summary' ? <CategoryIcon /> : <TableIcon />}
                        </Avatar>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Filters */}
              <Paper sx={{ mb: 3, overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Badge badgeContent={activeFilterCount} color="primary">
                        <FilterIcon color="action" />
                      </Badge>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Filters
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                  </Stack>
                </Box>

                {showFilters && (
                  <>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <DatePicker
                            label="Start Date"
                            value={filters.startDate}
                            onChange={(date) => handleFilterChange('startDate', date)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <DatePicker
                            label="End Date"
                            value={filters.endDate}
                            onChange={(date) => handleFilterChange('endDate', date)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Category</InputLabel>
                            <Select
                              value={filters.categoryId}
                              label="Category"
                              onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                            >
                              <MenuItem value="all">All Categories</MenuItem>
                              {categories.map(cat => (
                                <MenuItem key={cat.category_id} value={cat.category_id}>
                                  {cat.category_name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Item</InputLabel>
                            <Select
                              value={filters.itemId}
                              label="Item"
                              onChange={(e) => handleFilterChange('itemId', e.target.value)}
                            >
                              <MenuItem value="all">All Items</MenuItem>
                              {inventoryItems.map(item => (
                                <MenuItem key={item.item_id} value={item.item_id}>
                                  {item.item_name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        {viewMode === 'details' && (
                          <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Search"
                              value={filters.search}
                              onChange={(e) => handleFilterChange('search', e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                  </InputAdornment>
                                ),
                                endAdornment: filters.search && (
                                  <InputAdornment position="end">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleFilterChange('search', '')}
                                    >
                                      <ClearIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                )
                              }}
                            />
                          </Grid>
                        )}
                      </Grid>

                      <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {viewMode === 'summary' 
                            ? `Showing ${Array.isArray(salesSummary) ? salesSummary.length : 0} summary items`
                            : `Showing ${filteredRecords.length} transactions`}
                        </Typography>
                        <Button
                          size="small"
                          onClick={clearFilters}
                          startIcon={<ClearIcon />}
                        >
                          Clear All
                        </Button>
                      </Stack>
                    </Box>
                  </>
                )}
              </Paper>

              {/* Summary View */}
              {viewMode === 'summary' && (
                <Paper>
                  <TableContainer>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {groupBy === 'category' ? (
                            <>
                              <TableCell>Category</TableCell>
                              <TableCell align="right">Total Quantity</TableCell>
                              <TableCell align="right">Total Revenue (₦)</TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>Item</TableCell>
                              <TableCell>Category</TableCell>
                              <TableCell align="right">Total Quantity</TableCell>
                              <TableCell align="right">Total Revenue (₦)</TableCell>
                            </>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={groupBy === 'category' ? 3 : 4} align="center" sx={{ py: 8 }}>
                              <CircularProgress />
                            </TableCell>
                          </TableRow>
                        ) : !Array.isArray(salesSummary) || salesSummary.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={groupBy === 'category' ? 3 : 4} align="center" sx={{ py: 8 }}>
                              <Stack spacing={2} alignItems="center">
                                <MoneyIcon sx={{ fontSize: 48, color: theme.palette.grey[300] }} />
                                <Typography color="text.secondary">No sales data found</Typography>
                                <Button variant="outlined" size="small" onClick={clearFilters}>
                                  Clear Filters
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ) : (
                          salesSummary.map((item, index) => (
                            <TableRow key={index} hover>
                              {groupBy === 'category' ? (
                                <>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={500}>
                                      {item.category_name || 'Uncategorized'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      label={parseNumber(item.total_qty)}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" fontWeight={600} color="success.main">
                                      {formatNaira(parseNumber(item.total_cost))}
                                    </Typography>
                                  </TableCell>
                                </>
                              ) : (
                                <>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={500}>
                                      {item.item_name || 'Unknown'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={item.category_name || 'Uncategorized'}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      label={parseNumber(item.total_qty)}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" fontWeight={600} color="success.main">
                                      {formatNaira(parseNumber(item.total_cost))}
                                    </Typography>
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* Details View - WITH TOTAL REVENUE COLUMN */}
              {viewMode === 'details' && (
                <Paper>
                  <TableContainer>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Item</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Unit Price (₦)</TableCell>
                          <TableCell align="right">Total Revenue (₦)</TableCell>
                          <TableCell>Reported By</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                              <CircularProgress />
                            </TableCell>
                          </TableRow>
                        ) : paginatedRecords.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                              <Stack spacing={2} alignItems="center">
                                <MoneyIcon sx={{ fontSize: 48, color: theme.palette.grey[300] }} />
                                <Typography color="text.secondary">No sales records found</Typography>
                                <Button variant="outlined" size="small" onClick={clearFilters}>
                                  Clear Filters
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedRecords.map((record) => (
                            <TableRow key={record.id} hover>
                              <TableCell>
                                <Typography variant="body2">
                                  {format(safeParseDate(record.reported_on), 'MMM dd, yyyy')}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>
                                  {record.item_name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={record.category_name || 'Uncategorized'}
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={parseNumber(record.qty_sold)}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={600} color="primary.main">
                                  {formatNaira(parseNumber(record.cost))}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={700} color="success.main">
                                  {formatNaira(calculateTotalRevenue(record))}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                                    {record.reportedByName?.charAt(0) || '?'}
                                  </Avatar>
                                  <Typography variant="body2">
                                    {record.reportedByName || 'Unknown'}
                                  </Typography>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {filteredRecords.length > 0 && (
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                      <Pagination
                        count={pageCount}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  )}
                </Paper>
              )}
            </Container>

            {/* Refresh Indicator */}
            {refreshing && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 80,
                  right: 24,
                  zIndex: 1100
                }}
              >
                <Paper sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.9), color: 'white' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} color="inherit" />
                    <Typography variant="caption">Refreshing...</Typography>
                  </Stack>
                </Paper>
              </Box>
            )}

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                {snackbar.message}
              </Alert>
            </Snackbar>

          </Box>
        </LocalizationProvider>
      </IonContent>
    </IonPage>
  );
};

export default SalesDashboardBFSupervisor;